/**
 * 時系列の最大同時積載 (#428)。
 *
 * レーンが日単位 (#425) になり、配達で荷が減った後に積み直す運用が前提になった。
 * 「レーン内合計」での積載・商品数判定は全日レーンで常時誤発報するため、
 * 立寄り順に沿って pickup で加算・delivery で減算した系列の最大値で判定する。
 * admin の商品数警告と API の over_capacity 警告が同じ定義を共有する。
 */

/** pickup 系 stop (荷を積む)。cross_dock_pickup は hub での積み直し。 */
const PICKUP_STOP_TYPES = new Set(["pickup", "cross_dock_pickup"]);
/** delivery 系 stop (荷を降ろす)。cross_dock_drop は hub への引き渡し。 */
const DROP_STOP_TYPES = new Set(["delivery", "cross_dock_drop"]);

export interface MaxLoadStop {
  /** stop 種別。pickup 系で積み、delivery 系で降ろす。それ以外 (return 等) は無視。 */
  stop_type: string;
  /** 積載対象のキー (注文の public_id 等)。注文に紐づかない stop は null。 */
  order_key: string | null;
  /** この注文の積載量 (商品数・重量など単位は呼び出し側で統一)。不明は null (0 扱い)。 */
  amount: number | null;
}

/**
 * 立寄り順 (配列順) に沿った最大同時積載を返す。
 *
 * - 同一注文の量は複数 stop が持っていても二重計上せず最大値を採用する。
 * - レーン内に pickup 系 stop を持たない注文 (delivery-only, S8) は
 *   「出発時から積載」として初期積載に計上する。
 */
export function maxSimultaneousLoad(stops: readonly MaxLoadStop[]): number {
  const amountByOrder = new Map<string, number>();
  const hasPickupByOrder = new Map<string, boolean>();
  for (const stop of stops) {
    if (!stop.order_key) continue;
    amountByOrder.set(
      stop.order_key,
      Math.max(amountByOrder.get(stop.order_key) ?? 0, stop.amount ?? 0),
    );
    if (PICKUP_STOP_TYPES.has(stop.stop_type)) {
      hasPickupByOrder.set(stop.order_key, true);
    } else if (!hasPickupByOrder.has(stop.order_key)) {
      hasPickupByOrder.set(stop.order_key, false);
    }
  }

  // delivery-only 注文は出発デポで積込済み (S8) = 初期積載
  let running = 0;
  for (const [orderKey, hasPickup] of hasPickupByOrder) {
    if (!hasPickup) running += amountByOrder.get(orderKey) ?? 0;
  }

  let max = running;
  for (const stop of stops) {
    if (!stop.order_key) continue;
    const amount = amountByOrder.get(stop.order_key) ?? 0;
    if (PICKUP_STOP_TYPES.has(stop.stop_type)) running += amount;
    else if (DROP_STOP_TYPES.has(stop.stop_type)) running -= amount;
    if (running > max) max = running;
  }
  return max;
}
